import { generateClient } from 'aws-amplify/api';

// Lazy client initialization to ensure Amplify is configured first
let client = null;
const getClient = () => {
  if (!client) {
    client = generateClient();
  }
  return client;
};

// GraphQL operations for admin achievement management
const CREATE_ACHIEVEMENT_DEFINITION = `
  mutation CreateAchievementDefinition($input: CreateAchievementDefinitionInput!) {
    createAchievementDefinition(input: $input) {
      id
      name
      description
      category
      rarity
      points
      icon
      conditions
      conditionType
      actionType
      targetValue
      targetField
      contentIds
      genreName
      franchiseId
      isActive
      isRepeatable
      maxProgress
      createdAt
    }
  }
`;

const UPDATE_ACHIEVEMENT_DEFINITION = `
  mutation UpdateAchievementDefinition($input: UpdateAchievementDefinitionInput!) {
    updateAchievementDefinition(input: $input) {
      id
      name
      description
      category
      rarity
      points
      icon
      conditions
      conditionType
      actionType
      targetValue
      targetField
      contentIds
      genreName
      franchiseId
      isActive
      isRepeatable
      maxProgress
      threshold
      updatedAt
    }
  }
`;

const DELETE_ACHIEVEMENT_DEFINITION = `
  mutation DeleteAchievementDefinition($input: DeleteAchievementDefinitionInput!) {
    deleteAchievementDefinition(input: $input) {
      id
    }
  }
`;

const LIST_ACHIEVEMENT_DEFINITIONS = `
  query ListAchievementDefinitions {
    listAchievementDefinitions {
      items {
        id
        name
        description
        category
        rarity
        points
        icon
        conditions
        conditionType
        actionType
        targetValue
        targetField
        contentIds
        genreName
        franchiseId
        isActive
        isRepeatable
        maxProgress
        threshold
        createdAt
        updatedAt
      }
    }
  }
`;

const LIST_BADGE_DEFINITIONS = `
  query ListBadgeDefinitions {
    listBadgeDefinitions {
      items {
        id
        type
        name
        description
        icon
        color
        rarity
        points
        isActive
        autoAssign
        conditions
        createdAt
        updatedAt
      }
    }
  }
`;

const LIST_TITLE_DEFINITIONS = `
  query ListTitleDefinitions {
    listTitleDefinitions {
      items {
        id
        type
        name
        description
        color
        rarity
        minAchievements
        minPoints
        requiredAchievements
        isActive
        isDefault
        createdAt
        updatedAt
      }
    }
  }
`;

const LIST_ALL_USERS = `
  query ListUsers {
    listUsers {
      items {
        id
        username
        displayName
        email
        totalAchievementPoints
        leaderboardPoints
        followersCount
        reviewsCount
        moviesWatched
        titles
        titleOrder
        activeBadges
        badgeDisplaySettings
        createdAt
        updatedAt
      }
    }
  }
`;

export const adminAchievementService = {
  // Helper functions for creating specific achievement types
  createSpecificMovieAchievement(movieId, movieName) {
    return {
      name: `WATCH_${movieName.toUpperCase().replace(/\s+/g, '_')}`,
      description: `Watch ${movieName}`,
      category: 'CONTENT',
      rarity: 'uncommon',
      points: 15,
      icon: 'Movie',
      conditionType: 'SPECIFIC_MOVIE_WATCHED',
      contentIds: [movieId],
      conditions: [{
        type: 'SPECIFIC_MOVIE_WATCHED',
        field: 'movieID',
        operator: 'eq',
        value: movieId
      }],
      isActive: true,
      isRepeatable: false,
      maxProgress: 1
    };
  },

  createSpecificShowAchievement(showId, showName) {
    return {
      name: `WATCH_${showName.toUpperCase().replace(/\s+/g, '_')}`,
      description: `Watch ${showName}`,
      category: 'CONTENT',
      rarity: 'uncommon',
      points: 20,
      icon: 'Tv',
      conditionType: 'SPECIFIC_SHOW_WATCHED',
      contentIds: [showId],
      conditions: [{
        type: 'SPECIFIC_SHOW_WATCHED',
        field: 'showID',
        operator: 'eq',
        value: showId
      }],
      isActive: true,
      isRepeatable: false,
      maxProgress: 1
    };
  },

  createMovieGroupAchievement(movieIds, groupName, description) {
    return {
      name: `COMPLETE_${groupName.toUpperCase().replace(/\s+/g, '_')}`,
      description: description || `Complete watching all movies in ${groupName}`,
      category: 'COLLECTION',
      rarity: 'rare',
      points: movieIds.length * 10,
      icon: 'CollectionsBookmark',
      conditionType: 'MOVIE_GROUP_WATCHED',
      contentIds: movieIds,
      conditions: [{
        type: 'MOVIE_GROUP_WATCHED',
        field: 'movieGroup',
        operator: 'eq',
        value: movieIds
      }],
      isActive: true,
      isRepeatable: false,
      maxProgress: 1
    };
  },

  createGenreAchievement(genre, count) {
    return {
      name: `${genre.toUpperCase()}_ENTHUSIAST`,
      description: `Watch ${count} ${genre} movies`,
      category: 'GENRE',
      rarity: count >= 50 ? 'epic' : count >= 20 ? 'rare' : 'uncommon',
      points: count * 2,
      icon: 'Category',
      conditionType: 'GENRE_WATCHED_COUNT',
      targetValue: count,
      genreName: genre.toLowerCase(),
      conditions: [{
        type: 'GENRE_WATCHED_COUNT',
        field: genre.toLowerCase(),
        operator: 'gte',
        value: count
      }],
      isActive: true,
      isRepeatable: false,
      maxProgress: 1
    };
  },

  createFranchiseAchievement(franchiseId, franchiseName) {
    return {
      name: `${franchiseName.toUpperCase().replace(/\s+/g, '_')}_COMPLETIONIST`,
      description: `Complete the entire ${franchiseName} franchise`,
      category: 'FRANCHISE',
      rarity: 'legendary',
      points: 100,
      icon: 'MovieFilter',
      conditionType: 'FRANCHISE_WATCHED',
      franchiseId: franchiseId,
      conditions: [{
        type: 'FRANCHISE_WATCHED',
        field: 'franchiseID',
        operator: 'eq',
        value: franchiseId
      }],
      isActive: true,
      isRepeatable: false,
      maxProgress: 1
    };
  },

  createStreakAchievement(days, tier = 'common') {
    const tierConfig = {
      common: { points: 25, rarity: 'common', name: 'Streak Starter' },
      rare: { points: 100, rarity: 'rare', name: 'Streak Master' },
      legendary: { points: 500, rarity: 'legendary', name: 'Streak Legend' }
    };
    
    const config = tierConfig[tier] || tierConfig.common;
    
    return {
      name: `LOGIN_STREAK_${days}`,
      description: `Login for ${days} consecutive days`,
      category: 'ENGAGEMENT',
      rarity: config.rarity,
      points: config.points,
      icon: 'LocalFireDepartment',
      conditionType: 'LOGIN_STREAK',
      targetValue: days,
      targetField: 'loginStreak',
      conditions: [{
        type: 'LOGIN_STREAK',
        field: 'loginStreak',
        operator: 'gte',
        value: days
      }],
      isActive: true,
      isRepeatable: false,
      maxProgress: 1
    };
  },

  createHypeHunterAchievement(discoveries, tier = 'uncommon') {
    const tierConfig = {
      uncommon: { points: 50, rarity: 'uncommon', name: 'Early Bird' },
      rare: { points: 125, rarity: 'rare', name: 'Hype Hunter' },
      epic: { points: 300, rarity: 'epic', name: 'Trend Prophet' }
    };
    
    const config = tierConfig[tier] || tierConfig.uncommon;
    
    return {
      name: `EARLY_DISCOVERIES_${discoveries}`,
      description: `Discover ${discoveries} trending movies before they peak`,
      category: 'DISCOVERY',
      rarity: config.rarity,
      points: config.points,
      icon: 'TrendingUp',
      conditionType: 'EARLY_DISCOVERIES',
      targetValue: discoveries,
      targetField: 'earlyDiscoveries',
      conditions: [{
        type: 'EARLY_DISCOVERIES',
        field: 'earlyDiscoveries',
        operator: 'gte',
        value: discoveries
      }],
      isActive: true,
      isRepeatable: false,
      maxProgress: 1
    };
  },

  // Achievement Definition Management
  async createAchievement(achievementData) {
    try {
      const input = {
        name: achievementData.name || 'New Achievement',
        description: achievementData.description || 'Achievement description',
        category: achievementData.category || 'SOCIAL',
        rarity: achievementData.rarity || 'common',
        points: parseInt(achievementData.points) || 10,
        icon: achievementData.icon || 'EmojiEvents',
        conditions: achievementData.conditions ? JSON.stringify(achievementData.conditions) : JSON.stringify([]),
        conditionType: achievementData.conditionType,
        actionType: achievementData.actionType || undefined,
        targetValue: achievementData.targetValue ? parseInt(achievementData.targetValue) : undefined,
        targetField: achievementData.targetField || undefined,
        contentIds: achievementData.contentIds || undefined,
        genreName: achievementData.genreName || undefined,
        franchiseId: achievementData.franchiseId || undefined,
        isActive: achievementData.isActive !== undefined ? achievementData.isActive : true,
        isRepeatable: achievementData.isRepeatable !== undefined ? achievementData.isRepeatable : false,
        maxProgress: parseInt(achievementData.maxProgress) || 1
      };
      
      // Remove undefined values
      Object.keys(input).forEach(key => input[key] === undefined && delete input[key]);
      
      console.log('Creating achievement with input:', input);
      
      const result = await getClient().graphql({
        query: CREATE_ACHIEVEMENT_DEFINITION,
        variables: { input }
      });
      return result.data.createAchievementDefinition;
    } catch (error) {
      console.error('Error creating achievement:', error);
      if (error.errors) {
        error.errors.forEach((err, index) => {
          console.error(`GraphQL Error ${index + 1}:`, err.message);
          console.error('Error details:', err);
        });
      }
      console.error('Error data:', error.data);
      throw error;
    }
  },

  async updateAchievement(id, achievementData) {
    try {
      const input = {
        id,
        name: achievementData.name,
        description: achievementData.description,
        category: achievementData.category,
        rarity: achievementData.rarity,
        points: achievementData.points ? parseInt(achievementData.points) : undefined,
        icon: achievementData.icon,
        conditions: achievementData.conditions ? JSON.stringify(achievementData.conditions) : undefined,
        conditionType: achievementData.conditionType,
        actionType: achievementData.actionType,
        targetValue: achievementData.targetValue ? parseInt(achievementData.targetValue) : undefined,
        targetField: achievementData.targetField,
        contentIds: achievementData.contentIds,
        genreName: achievementData.genreName,
        franchiseId: achievementData.franchiseId,
        isActive: achievementData.isActive,
        isRepeatable: achievementData.isRepeatable,
        maxProgress: achievementData.maxProgress ? parseInt(achievementData.maxProgress) : undefined
      };
      
      Object.keys(input).forEach(key => input[key] === undefined && delete input[key]);
      
      const result = await getClient().graphql({
        query: UPDATE_ACHIEVEMENT_DEFINITION,
        variables: { input }
      });
      return result.data.updateAchievementDefinition;
    } catch (error) {
      console.error('Error updating achievement:', error);
      console.error('Full error object:', JSON.stringify(error, null, 2));
      if (error.errors) {
        console.error('GraphQL errors:', error.errors);
        error.errors.forEach((err, index) => {
          console.error(`GraphQL Error ${index + 1}:`, err.message);
          console.error('Error details:', err);
        });
      }
      throw error;
    }
  },

  async deleteAchievement(id) {
    try {
      await getClient().graphql({
        query: DELETE_ACHIEVEMENT_DEFINITION,
        variables: { input: { id } }
      });
      return true;
    } catch (error) {
      console.error('Error deleting achievement:', error);
      throw error;
    }
  },

  async getAllAchievements() {
    try {
      const result = await getClient().graphql({
        query: LIST_ACHIEVEMENT_DEFINITIONS
      });
      
      //console.log('Raw GraphQL result:', result.data.listAchievementDefinitions.items);
      
      return result.data.listAchievementDefinitions.items
        .filter(item => item.isActive !== false) // Include items where isActive is true or undefined
        .map(item => ({
          ...item,
          // Keep the original name as the key for frontend mapping
          type: item.name,
          conditions: item.conditions ? JSON.parse(item.conditions) : [],
          isRepeatable: item.isRepeatable || false,
          maxProgress: item.maxProgress || 1,
          threshold: item.threshold || 1,
          badgeType: item.badgeType || null,
          // Ensure standardized fields are available
          conditionType: item.conditionType,
          targetValue: item.targetValue,
          targetField: item.targetField,
          contentIds: item.contentIds || [],
          genreName: item.genreName || '',
          franchiseId: item.franchiseId || ''
        }));
    } catch (error) {
      console.error('Error fetching achievements:', error);
      console.error('Full error object:', JSON.stringify(error, null, 2));
      if (error.errors) {
        console.error('GraphQL errors:', error.errors);
        error.errors.forEach((err, index) => {
          console.error(`GraphQL Error ${index + 1}:`, err.message);
          console.error('Error details:', err);
        });
      }
      return [];
    }
  },

  // Content-specific achievement creation methods
  async createMovieAchievement(movieId, movieName) {
    const achievementData = this.createSpecificMovieAchievement(movieId, movieName);
    return await this.createAchievement(achievementData);
  },

  async createShowAchievement(showId, showName) {
    const achievementData = this.createSpecificShowAchievement(showId, showName);
    return await this.createAchievement(achievementData);
  },

  async createMovieCollectionAchievement(movieIds, collectionName, description) {
    const achievementData = this.createMovieGroupAchievement(movieIds, collectionName, description);
    return await this.createAchievement(achievementData);
  },

  async createMarvelCinematicUniverseAchievements() {
    const mcuPhase1 = ['1726', '1724', '1725', '10195', '1771', '24428'];
    return await this.createMovieCollectionAchievement(
      mcuPhase1,
      'MCU Phase 1',
      'Complete Phase 1 of the Marvel Cinematic Universe'
    );
  },

  async createStreakAchievements() {
    const streakTiers = [
      { days: 7, tier: 'common' },
      { days: 30, tier: 'rare' },
      { days: 100, tier: 'legendary' }
    ];
    
    const results = [];
    for (const { days, tier } of streakTiers) {
      try {
        const achievementData = this.createStreakAchievement(days, tier);
        const result = await this.createAchievement(achievementData);
        results.push(result);
      } catch (error) {
        console.error(`Error creating streak achievement for ${days} days:`, error);
      }
    }
    return results;
  },

  async createHypeHunterAchievements() {
    const hypeHunterTiers = [
      { discoveries: 10, tier: 'uncommon' },
      { discoveries: 25, tier: 'rare' },
      { discoveries: 50, tier: 'epic' }
    ];
    
    const results = [];
    for (const { discoveries, tier } of hypeHunterTiers) {
      try {
        const achievementData = this.createHypeHunterAchievement(discoveries, tier);
        const result = await this.createAchievement(achievementData);
        results.push(result);
      } catch (error) {
        console.error(`Error creating hype hunter achievement for ${discoveries} discoveries:`, error);
      }
    }
    return results;
  },

  // Badge Management
  async getAllBadges() {
    try {
      const result = await getClient().graphql({
        query: LIST_BADGE_DEFINITIONS
      });
      return result.data.listBadgeDefinitions.items.map(item => ({
        ...item,
        conditions: item.conditions ? JSON.parse(item.conditions) : {}
      }));
    } catch (error) {
      console.error('Error fetching badges:', error);
      return [];
    }
  },

  async createBadge(badgeData) {
    try {
      const CREATE_BADGE_DEFINITION = `
        mutation CreateBadgeDefinition($input: CreateBadgeDefinitionInput!) {
          createBadgeDefinition(input: $input) {
            id
            type
            name
            description
            icon
            color
            rarity
            points
            isActive
            autoAssign
            conditions
            createdAt
          }
        }
      `;
      
      const input = {
        type: badgeData.type || badgeData.name?.toUpperCase().replace(/\s+/g, '_') || 'CUSTOM_BADGE',
        name: badgeData.name || 'New Badge',
        description: badgeData.description || 'Badge description',
        icon: badgeData.icon || 'Star',
        color: badgeData.color || '#FFD700',
        rarity: badgeData.rarity || 'common',
        points: parseInt(badgeData.points) || 0,
        isActive: badgeData.isActive !== undefined ? badgeData.isActive : true,
        autoAssign: badgeData.autoAssign !== undefined ? badgeData.autoAssign : false,
        conditions: badgeData.conditions ? JSON.stringify(badgeData.conditions) : JSON.stringify({})
      };
      
      console.log('Creating badge with input:', input);
      
      const result = await getClient().graphql({
        query: CREATE_BADGE_DEFINITION,
        variables: { input }
      });
      return result.data.createBadgeDefinition;
    } catch (error) {
      console.error('Error creating badge:', error);
      if (error.errors) {
        error.errors.forEach((err, index) => {
          console.error(`GraphQL Error ${index + 1}:`, err.message);
        });
      }
      throw error;
    }
  },

  async updateBadge(id, badgeData) {
    try {
      const UPDATE_BADGE_DEFINITION = `
        mutation UpdateBadgeDefinition($input: UpdateBadgeDefinitionInput!) {
          updateBadgeDefinition(input: $input) {
            id
            type
            name
            description
            icon
            color
            rarity
            points
            isActive
            autoAssign
            conditions
            updatedAt
          }
        }
      `;
      
      const input = {
        id,
        name: badgeData.name,
        description: badgeData.description,
        icon: badgeData.icon,
        color: badgeData.color,
        rarity: badgeData.rarity,
        points: badgeData.points ? parseInt(badgeData.points) : undefined,
        isActive: badgeData.isActive,
        autoAssign: badgeData.autoAssign,
        conditions: badgeData.conditions ? JSON.stringify(badgeData.conditions) : undefined
      };
      
      Object.keys(input).forEach(key => input[key] === undefined && delete input[key]);
      
      const result = await getClient().graphql({
        query: UPDATE_BADGE_DEFINITION,
        variables: { input }
      });
      return result.data.updateBadgeDefinition;
    } catch (error) {
      console.error('Error updating badge:', error);
      throw error;
    }
  },

  async deleteBadge(id) {
    try {
      const DELETE_BADGE_DEFINITION = `
        mutation DeleteBadgeDefinition($input: DeleteBadgeDefinitionInput!) {
          deleteBadgeDefinition(input: $input) {
            id
          }
        }
      `;
      
      await getClient().graphql({
        query: DELETE_BADGE_DEFINITION,
        variables: { input: { id } }
      });
      return true;
    } catch (error) {
      console.error('Error deleting badge:', error);
      throw error;
    }
  },

  // Title Management  
  async getAllTitles() {
    try {
      const result = await getClient().graphql({
        query: LIST_TITLE_DEFINITIONS
      });
      return result.data.listTitleDefinitions.items;
    } catch (error) {
      console.error('Error fetching titles:', error);
      return [];
    }
  },

  async createTitle(titleData) {
    try {
      const CREATE_TITLE_DEFINITION = `
        mutation CreateTitleDefinition($input: CreateTitleDefinitionInput!) {
          createTitleDefinition(input: $input) {
            id
            type
            name
            description
            color
            rarity
            minAchievements
            minPoints
            requiredAchievements
            isActive
            isDefault
            createdAt
          }
        }
      `;
      
      const input = {
        type: titleData.type || titleData.name?.toUpperCase().replace(/\s+/g, '_') || 'CUSTOM_TITLE',
        name: titleData.name || 'New Title',
        description: titleData.description || 'Title description',
        color: titleData.color || '#1976d2',
        rarity: titleData.rarity || 'common',
        minAchievements: parseInt(titleData.minAchievements) || 0,
        minPoints: parseInt(titleData.minPoints) || 0,
        requiredAchievements: titleData.requiredAchievements || [],
        isActive: titleData.isActive !== undefined ? titleData.isActive : true,
        isDefault: titleData.isDefault !== undefined ? titleData.isDefault : false
      };
      
      console.log('Creating title with input:', input);
      
      const result = await getClient().graphql({
        query: CREATE_TITLE_DEFINITION,
        variables: { input }
      });
      return result.data.createTitleDefinition;
    } catch (error) {
      console.error('Error creating title:', error);
      if (error.errors) {
        error.errors.forEach((err, index) => {
          console.error(`GraphQL Error ${index + 1}:`, err.message);
        });
      }
      throw error;
    }
  },

  async updateTitle(id, titleData) {
    try {
      const UPDATE_TITLE_DEFINITION = `
        mutation UpdateTitleDefinition($input: UpdateTitleDefinitionInput!) {
          updateTitleDefinition(input: $input) {
            id
            type
            name
            description
            color
            rarity
            minAchievements
            minPoints
            requiredAchievements
            isActive
            isDefault
            updatedAt
          }
        }
      `;
      
      const input = {
        id,
        name: titleData.name,
        description: titleData.description,
        color: titleData.color,
        rarity: titleData.rarity,
        minAchievements: titleData.minAchievements ? parseInt(titleData.minAchievements) : undefined,
        minPoints: titleData.minPoints ? parseInt(titleData.minPoints) : undefined,
        requiredAchievements: titleData.requiredAchievements,
        isActive: titleData.isActive,
        isDefault: titleData.isDefault
      };
      
      Object.keys(input).forEach(key => input[key] === undefined && delete input[key]);
      
      const result = await getClient().graphql({
        query: UPDATE_TITLE_DEFINITION,
        variables: { input }
      });
      return result.data.updateTitleDefinition;
    } catch (error) {
      console.error('Error updating title:', error);
      throw error;
    }
  },

  async deleteTitle(id) {
    try {
      const DELETE_TITLE_DEFINITION = `
        mutation DeleteTitleDefinition($input: DeleteTitleDefinitionInput!) {
          deleteTitleDefinition(input: $input) {
            id
          }
        }
      `;
      
      await getClient().graphql({
        query: DELETE_TITLE_DEFINITION,
        variables: { input: { id } }
      });
      return true;
    } catch (error) {
      console.error('Error deleting title:', error);
      throw error;
    }
  },

  // User Management
  async getAllUsers() {
    try {
      const result = await getClient().graphql({
        query: LIST_ALL_USERS
      });
      // Filter out null items and corrupted users (missing required fields)
      const users = (result.data?.listUsers?.items || [])
        .filter(user => user !== null && user.username && user.email);
      return users;
    } catch (error) {
      console.error('Error fetching users:', error);
      // If there are errors but data exists, return valid users only
      if (error.data?.listUsers?.items) {
        return error.data.listUsers.items
          .filter(user => user !== null && user.username && user.email);
      }
      return [];
    }
  },

  async getUserAchievements(userId) {
    try {
      const LIST_USER_ACHIEVEMENTS = `
        query ListUserAchievements($userId: ID!) {
          listUserAchievements(filter: { userID: { eq: $userId } }) {
            items {
              id
              userID
              achievementType
              unlocked
              unlockedAt
              progress
              createdAt
            }
          }
        }
      `;
      
      const result = await getClient().graphql({
        query: LIST_USER_ACHIEVEMENTS,
        variables: { userId }
      });
      return result.data.listUserAchievements.items;
    } catch (error) {
      console.error('Error fetching user achievements:', error);
      return [];
    }
  },

  async grantAchievement(userId, achievementType) {
    try {
      const CREATE_USER_ACHIEVEMENT = `
        mutation CreateUserAchievement($input: CreateUserAchievementInput!) {
          createUserAchievement(input: $input) {
            id
            userID
            achievementType
            unlocked
            unlockedAt
            progress
            createdAt
          }
        }
      `;
      
      const input = {
        userID: userId,
        achievementType: achievementType,
        unlocked: true,
        unlockedAt: new Date().toISOString(),
        progress: 1
      };
      
      console.log('Granting achievement with input:', input);
      
      const result = await getClient().graphql({
        query: CREATE_USER_ACHIEVEMENT,
        variables: { input }
      });
      return result.data.createUserAchievement;
    } catch (error) {
      console.error('Error granting achievement:', error);
      console.error('Full error object:', JSON.stringify(error, null, 2));
      if (error.errors) {
        error.errors.forEach((err, index) => {
          console.error(`GraphQL Error ${index + 1}:`, err.message);
        });
      }
      throw error;
    }
  },

  async grantBadge(userId, badgeType) {
    try {
      // Get badge definition for notification
      const badgeDefinition = await this.getAllBadges().then(badges => 
        badges.find(b => b.type === badgeType)
      );
      
      const CREATE_USER_BADGE = `
        mutation CreateUserBadge($input: CreateUserBadgeInput!) {
          createUserBadge(input: $input) {
            id
            userID
            badgeType
            earnedAt
          }
        }
      `;
      
      const input = {
        userID: userId,
        badgeType: badgeType,
        earnedAt: new Date().toISOString()
      };
      
      console.log('Granting badge with input:', input);
      
      const result = await getClient().graphql({
        query: CREATE_USER_BADGE,
        variables: { input }
      });
      
      // Trigger badge notification
      if (badgeDefinition) {
        const badgeData = {
          name: badgeDefinition.name,
          description: badgeDefinition.description,
          color: badgeDefinition.color,
          rarity: badgeDefinition.rarity,
          points: badgeDefinition.points,
          badgeType: badgeType
        };
        
        // Dispatch custom event for badge notification
        const event = new CustomEvent('badgeEarned', { detail: badgeData });
        window.dispatchEvent(event);
      }
      
      return result.data.createUserBadge;
    } catch (error) {
      console.error('Error granting badge:', error);
      console.error('Full error object:', JSON.stringify(error, null, 2));
      if (error.errors) {
        error.errors.forEach((err, index) => {
          console.error(`GraphQL Error ${index + 1}:`, err.message);
        });
      }
      throw error;
    }
  },

  async updateUserTitle(userId, titleType) {
    try {
      // Get current user data first
      const GET_USER = `
        query GetUser($id: ID!) {
          getUser(id: $id) {
            id
            titles
            totalAchievementPoints
          }
        }
      `;
      
      const userResult = await getClient().graphql({
        query: GET_USER,
        variables: { id: userId }
      });
      
      const user = userResult.data?.getUser;
      const previousTitle = user?.titles?.[0] || 'Newcomer';
      
      // Update user title
      const UPDATE_USER = `
        mutation UpdateUser($input: UpdateUserInput!) {
          updateUser(input: $input) {
            id
            titles
            titleUpdatedAt
            updatedAt
          }
        }
      `;
      
      const input = {
        id: userId,
        titles: [titleType],
        titleUpdatedAt: new Date().toISOString(),
        titleSource: 'ADMIN'
      };
      
      console.log('Updating user title with input:', input);
      
      const result = await getClient().graphql({
        query: UPDATE_USER,
        variables: { input }
      });
      
      // Create title history record
      const CREATE_TITLE_HISTORY = `
        mutation CreateUserTitleHistory($input: CreateUserTitleHistoryInput!) {
          createUserTitleHistory(input: $input) {
            id
            userID
            previousTitle
            newTitle
            reason
            createdAt
          }
        }
      `;
      
      await getClient().graphql({
        query: CREATE_TITLE_HISTORY,
        variables: {
          input: {
            userID: userId,
            previousTitle: previousTitle,
            newTitle: titleType,
            achievementCount: 0,
            totalPoints: user?.totalAchievementPoints || 0,
            reason: 'Manual admin assignment'
          }
        }
      });
      
      return result.data.updateUser;
    } catch (error) {
      console.error('Error updating user title:', error);
      console.error('Full error object:', JSON.stringify(error, null, 2));
      if (error.errors) {
        error.errors.forEach((err, index) => {
          console.error(`GraphQL Error ${index + 1}:`, err.message);
        });
      }
      throw error;
    }
  },

  async getUserBadges(userId) {
    try {
      const LIST_USER_BADGES = `
        query ListUserBadges($userId: ID!) {
          listUserBadges(filter: { userID: { eq: $userId } }) {
            items {
              id
              userID
              badgeType
              earnedAt
            }
          }
        }
      `;
      
      const result = await getClient().graphql({
        query: LIST_USER_BADGES,
        variables: { userId }
      });
      return result.data.listUserBadges.items;
    } catch (error) {
      console.error('Error fetching user badges:', error);
      return [];
    }
  },

  async revokeAchievement(achievementId) {
    try {
      const DELETE_USER_ACHIEVEMENT = `
        mutation DeleteUserAchievement($input: DeleteUserAchievementInput!) {
          deleteUserAchievement(input: $input) {
            id
          }
        }
      `;
      
      await getClient().graphql({
        query: DELETE_USER_ACHIEVEMENT,
        variables: { input: { id: achievementId } }
      });
      return true;
    } catch (error) {
      console.error('Error revoking achievement:', error);
      throw error;
    }
  },

  async revokeBadge(badgeId) {
    try {
      const DELETE_USER_BADGE = `
        mutation DeleteUserBadge($input: DeleteUserBadgeInput!) {
          deleteUserBadge(input: $input) {
            id
          }
        }
      `;
      
      await getClient().graphql({
        query: DELETE_USER_BADGE,
        variables: { input: { id: badgeId } }
      });
      return true;
    } catch (error) {
      console.error('Error revoking badge:', error);
      throw error;
    }
  },

  async removeUserTitle(userId) {
    try {
      const UPDATE_USER = `
        mutation UpdateUser($input: UpdateUserInput!) {
          updateUser(input: $input) {
            id
            titles
            updatedAt
          }
        }
      `;
      
      const input = {
        id: userId,
        titles: []
      };
      
      const result = await getClient().graphql({
        query: UPDATE_USER,
        variables: { input }
      });
      return result.data.updateUser;
    } catch (error) {
      console.error('Error removing user title:', error);
      throw error;
    }
  },

  // Analytics
  async getAnalytics() {
    try {
      const [achievements, users] = await Promise.all([
        this.getAllAchievements(),
        this.getAllUsers()
      ]);
      
      return {
        totalAchievements: (achievements || []).length,
        totalUsers: (users || []).length,
        topAchievements: (achievements || []).slice(0, 10),
        recentUnlocks: [],
        achievements: achievements || [],
        users: users || []
      };
    } catch (error) {
      console.error('Error fetching analytics:', error);
      return {
        totalAchievements: 0,
        totalUsers: 0,
        topAchievements: [],
        recentUnlocks: [],
        achievements: [],
        users: []
      };
    }
  },

  // Admin Cheat Functions
  async getCheatHistory() {
    // Return empty array for now - implement proper logging later
    return [];
  },

  async instantUnlockAchievements({ userId, achievementTypes, adminReason }) {
    const results = [];
    for (const achievementType of achievementTypes) {
      try {
        const result = await this.grantAchievement(userId, achievementType.name || achievementType);
        results.push(result);
      } catch (error) {
        console.error(`Failed to unlock ${achievementType}:`, error);
      }
    }
    return results;
  },

  async applyProgressBoost({ userId, multiplier, durationHours, adminReason }) {
    // Store boost in user metadata - implement in User table later
    console.log('Progress boost applied:', { userId, multiplier, durationHours, adminReason });
    return { success: true, message: 'Progress boost applied (feature pending)' };
  },

  async manipulateUserStats({ userId, statChanges, adminReason }) {
    try {
      const UPDATE_USER = `
        mutation UpdateUser($input: UpdateUserInput!) {
          updateUser(input: $input) {
            id
            followersCount
            reviewsCount
            moviesWatched
            totalAchievementPoints
          }
        }
      `;
      
      const input = { id: userId };
      if (statChanges.followersCount) input.followersCount = statChanges.followersCount;
      if (statChanges.reviewsCount) input.reviewsCount = statChanges.reviewsCount;
      if (statChanges.moviesWatched) input.moviesWatched = statChanges.moviesWatched;
      if (statChanges.totalAchievementPoints) input.totalAchievementPoints = statChanges.totalAchievementPoints;
      
      const result = await getClient().graphql({
        query: UPDATE_USER,
        variables: { input }
      });
      return result.data.updateUser;
    } catch (error) {
      console.error('Error manipulating user stats:', error);
      throw error;
    }
  },

  async toggleGlobalBoost({ active, multiplier, adminReason }) {
    // Store in system config - implement later
    console.log('Global boost toggled:', { active, multiplier, adminReason });
    return { success: true, active, multiplier };
  },

  async bulkUnlockAllAchievements({ adminReason }) {
    try {
      const [users, achievements] = await Promise.all([
        this.getAllUsers(),
        this.getAllAchievements()
      ]);
      
      let count = 0;
      for (const user of users) {
        for (const achievement of achievements) {
          try {
            await this.grantAchievement(user.id, achievement.name);
            count++;
          } catch (error) {
            // Skip if already exists
          }
        }
      }
      
      return { success: true, count, message: `Unlocked ${count} achievements` };
    } catch (error) {
      console.error('Error with bulk unlock:', error);
      throw error;
    }
  }
};